<template>
  <div :class="['sidebar', { closed: !isSideNavOpen },'print-never']">
    <div class="sidebar-header">
      <div
        id="burger"
        :class="['float-right', { active: isSideNavOpen }]"
        @click.prevent="toggle"
      >
        <slot name="burger">
          <button type="button" class="burger-button" title="Menu">
            <span class="burger-bar burger-bar--1"></span>
            <span class="burger-bar burger-bar--3"></span>
          </button>
        </slot>
      </div>

      <slot name="logo">
        <transition name="logo">
          <router-link class="d-block" to="/">
            <div v-if="isSideNavOpen" class="sidebar-logo">
              <img
                class="img-fluid pr-3 py-3"
                src="../../assets/logo.png"
                alt="Logo"
              />
            </div>
          </router-link>
        </transition>
      </slot>
    </div>

    <slot name="menu">
      <div class="sidebar-content overflow-auto mb-2">
        <ul class="list-unstyled components">
          <li v-for="(route, index) in routes" :key="index">
            <template v-if="route.children">
              <a
                class="d-block cursor-pointer"
                v-b-toggle="'collapse-' + index"
                @click.stop.prevent
              >
                <span class="d-inline-block">
                  <render-icon
                    :icon="route.meta.icon"
                    :color="'#A4B8D1'"
                  ></render-icon>
                </span>

                <transition name="item">
                  <span class="d-inline-block" v-if="isSideNavOpen">{{
                    route.name
                  }}</span>
                </transition>
              </a>
              <b-collapse :id="'collapse-' + index">
                <li
                  :class="[{ 'pl-3': isSideNavOpen }]"
                  v-for="child in route.children"
                  :key="child.name"
                >
                  <router-link class="d-block" :to="{ name: child.name }">
                    <span class="d-inline-block">
                      <render-icon
                        :icon="child.meta.icon"
                        :color="'#A4B8D1'"
                      ></render-icon>
                    </span>
                    <transition name="item">
                      <span class="d-inline-block" v-if="isSideNavOpen">{{
                        child.name
                      }}</span>
                    </transition>
                  </router-link>
                </li>
              </b-collapse>
            </template>

            <!-- if route has no children -->
            <router-link
              v-else
              class="d-block"
              :to="{ name: route.name }"
              exact
            >
              <span class="d-inline-block">
                <render-icon
                  :icon="route.meta.icon"
                  :color="'#A4B8D1'"
                ></render-icon>
              </span>
              <transition name="item">
                <span class="d-inline-block" v-if="isSideNavOpen">{{
                  route.name
                }}</span>
              </transition>
            </router-link>
          </li>
        </ul>
      </div>
    </slot>

    <div class="sidebar-footer overflow-hidden">
      <template v-if="isAuthorizedToViewLinks">
        <a
          v-if="routes.length > 0"
          id="customer-btn"
          :class="['overflow-hidden', { 'text-center': !isSideNavOpen }]"
          @click.prevent
          v-b-modal.modal-customer-lock
          href=""
        >
          <span class="d-inline-block">
            <UserHostIcon
              :className="'px-2 pb-1'"
              :size="32"
              :color="'#A4B8D1'"
            ></UserHostIcon>
          </span>
          <transition name="item">
            <span
              v-if="isSideNavOpen"
              :class="['d-inline-block', 'customer-info']"
              >Customer Info/Change</span
            >
          </transition>
        </a>
        <b-popover
          v-if="routes.length > 0"
          custom-class="customer-popover"
          target="customer-btn"
          triggers="hover"
          boundary="viewport"
          :placement="isSideNavOpen ? 'top' : 'right'"
        >
          <template v-slot:title>
            <span v-if="getCustomer.customerName"
              >Customer: {{ getCustomer.customerName }}</span
            >
            <span v-else>No customer</span>
          </template>
          <div v-if="getCustomer.customerName">
            <strong>Id:</strong> {{ getCustomer.cid }}<br />
            <strong>Pbx:</strong>
            <span v-if="getCustomer.pbx">{{ getCustomer.pbx.name }}</span>
          </div>
        </b-popover>
      </template>
      <a
        :class="[
          'overflow-hidden customer-info',
          { 'text-center': !isSideNavOpen },
        ]"
        @click.prevent
        v-b-modal.modal-feedback
        href=""
      >
        <span class="d-inline-block">
          <FeedbackIcon
            :className="'px-2 pb-2'"
            :size="32"
            :color="'#A4B8D1'"
          ></FeedbackIcon>
        </span>
        <transition name="item">
          <span v-if="isSideNavOpen" :class="['d-inline-block']">Feedback</span>
        </transition>
      </a>
      <template v-if="isSuperAdmin">
        <a
          :href="jobRunrDashboardUrl"
          target="_blank"
          :class="[
            'overflow-hidden customer-info',
            { 'text-center': !isSideNavOpen },
          ]"
        >
          <span class="d-inline-block">
            <NewTabIcon
              :className="'px-2 pb-2'"
              :size="32"
              :color="'#A4B8D1'"
            />
          </span>
          <transition name="item">
            <span v-if="isSideNavOpen" :class="['d-inline-block']"
              >JobRunr Dashboard</span
            >
          </transition>
        </a>
      </template>
      <transition name="item">
        <a
          href="https://tap-smp-diagnostics.8x8.com/smpdiagnostics/report/home"
          target="_blank"
          :class="[
            'overflow-hidden customer-info',
            { 'text-center': !isSideNavOpen },
          ]"
        >
          <span class="d-inline-block">
            <NewTabIcon
              :className="'px-2 pb-2'"
              :size="32"
              :color="'#A4B8D1'"
            />
          </span>
          <transition name="item">
            <span v-if="isSideNavOpen" :class="['d-inline-block']"
              >SMP Diagnostics</span
            >
          </transition>
        </a>
      </transition>
      <a
        :class="['overflow-hidden ', { 'text-center': !isSideNavOpen }]"
        @click.prevent="logout"
        href=""
      >
        <span class="d-inline-block">
          <LeaveIcon
            :className="'px-2 pb-1'"
            :size="32"
            :color="'#A4B8D1'"
          ></LeaveIcon>
        </span>
        <transition name="item">
          <span v-if="isSideNavOpen" :class="['d-inline-block', 'logout']"
            >Logout</span
          >
        </transition>
      </a>
      <div :class="['copyright', { 'pl-2': isSideNavOpen }]">
        <transition name="item">
          <span
            >©<br v-if="!isSideNavOpen" />
            8x8
          </span>
        </transition>
        <transition name="item">
          <span :class="['pl-1', 'd-inline-block']" v-if="isSideNavOpen">
            {{ currentYear }}</span
          >
        </transition>
      </div>
      <div v-if="isSideNavOpen" :class="['copyright pl-2']">
        <transition name="item">
          <span>Version: {{ version }}</span>
        </transition>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import { routes } from "@/vue/router";

import { mapActions, mapGetters } from "vuex";
import { TAP_ALL, TAP_TOOLS_SUPER } from "@/react/services/rbac/groups";
import { API_V2_URL } from "@/vue/store/urls";
import { getEnv } from '@/env';

Vue.component("render-icon", {
  render: function (createElement) {
    return createElement(this.icon, {
      attrs: {
        color: this.color,
        size: 32,
        className: "px-2 pb-1",
      },
    });
  },
  props: {
    icon: {
      required: true,
    },
    color: {
      type: String,
    },
  },
});

export default {
  data() {
    return {};
  },
  computed: {
    ...mapGetters(["isSideNavOpen", "getCustomer", "getUser"]),
    jobRunrDashboardUrl() {
      return `${API_V2_URL}/jobrunr/dashboard`;
    },
    routes() {
      return routes.filter((route) => {
        if (
          this.getUser.groups &&
          route.children &&
          route.children.length > 0
        ) {
          route.children = this._.filter(route.children, (child) => {
            if (child.meta.groups.length === 0) {
              return true;
            }
            return (
              this._.intersection(this.getUser.groups, child.meta.groups)
                .length > 0
            );
          });
        }
        return (
          this._.intersection(this.getUser.groups, route.meta.groups).length >
            0 || route.meta.groups?.length === 0
        );
      });
    },
    version() {
      return getEnv("VUE_APP_VERSION");
    },
    currentYear() {
      return new Date().getFullYear();
    },
    isAuthorizedToViewLinks() {
      return this._.intersection(this.getUser.groups, TAP_ALL).length > 0;
    },
    isSuperAdmin() {
      return this.getUser.groups.includes(TAP_TOOLS_SUPER);
    },
  },
  methods: {
    ...mapActions(["setSideNavOpen"]),
    toggle() {
      this.setSideNavOpen(!this.isSideNavOpen);
    },
  },
};
</script>
